import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import TradingModal from '../../../components/TradingModal';
import MarketWatchSettingsModal from './MarketWatchSettingsModal';
import tradingApi from '../../../services/tradingApi';
import useLanguage from '../../../hooks/useLanguage';

const SymbolList = ({ onSymbolSelect, selectedSymbol }) => {
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [tradingModalOpen, setTradingModalOpen] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [tradingType, setTradingType] = useState('buy');
  const [selectedSymbolForTrade, setSelectedSymbolForTrade] = useState(null);
  const [symbols, setSymbols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [error, setError] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(['fx', 'crypto', 'popular']); // Default expanded
  const { t, isRTL } = useLanguage();

  useEffect(() => {
    initializeSymbolList();
    const cleanup = connectToWebSocket();

    return () => {
      tradingApi?.disconnectWebSocket();
      if (cleanup) cleanup();
    };
  }, []);

  const initializeSymbolList = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch symbols from /public/symbols endpoint
      const response = await fetch('/symbols/index.json');
      
      if (!response?.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const symbolsData = await response?.json();
      
      if (Array.isArray(symbolsData)) {
        // Process API response
        const processedSymbols = symbolsData?.map(symbol => ({
          symbol: symbol?.symbol || 'UNKNOWN',
          bid: parseFloat(symbol?.bid) || 0,
          ask: parseFloat(symbol?.ask) || 0,
          change: parseFloat(symbol?.change) || 0,
          changePercent: parseFloat(symbol?.changePercent) || 0,
          volume: parseInt(symbol?.volume) || 0,
          category: symbol?.category || 'forex',
          isFavorite: false, // Default to false, can be loaded from user preferences
          description: symbol?.description || symbol?.symbol
        }));
        
        setSymbols(processedSymbols);
      } else {
        throw new Error('Invalid response format from symbols endpoint');
      }
    } catch (apiError) {
      console.warn('Public symbols endpoint loading failed, using fallback data:', apiError);
      setError('Symbols endpoint failed - using demo data');
      
      // Keep existing fallback mock data
      const mockSymbols = [
        { symbol: 'EURUSD', bid: 1.16745, ask: 1.16753, change: 0.0008, changePercent: 0.07, volume: 1250000, isFavorite: true, category: 'forex' },
        { symbol: 'GBPUSD', bid: 1.34281, ask: 1.34289, change: -0.0012, changePercent: -0.09, volume: 980000, isFavorite: true, category: 'forex' },
        { symbol: 'XAUUSD', bid: 1935.02, ask: 1935.23, change: 12.45, changePercent: 0.64, volume: 750000, isFavorite: true, category: 'metals' },
        { symbol: 'XAGUSD', bid: 48.329, ask: 48.342, change: -0.234, changePercent: -0.48, volume: 320000, isFavorite: false, category: 'metals' },
        { symbol: 'GBPJPY', bid: 201.887, ask: 201.903, change: 0.456, changePercent: 0.23, volume: 560000, isFavorite: true, category: 'forex' },
        { symbol: 'GER30', bid: 23823.8, ask: 23825.4, change: -45.2, changePercent: -0.28, volume: 420000, isFavorite: true, category: 'indices' },
        { symbol: 'US500', bid: 6135.6, ask: 6136.2, change: 23.4, changePercent: 0.57, volume: 890000, isFavorite: false, category: 'indices' },
        { symbol: 'USDJPY', bid: 151.234, ask: 151.247, change: 0.456, changePercent: 0.30, volume: 456789, isFavorite: false, category: 'forex' },
        { symbol: 'AUDUSD', bid: 0.65432, ask: 0.65438, change: -0.0015, changePercent: -0.23, volume: 345670, isFavorite: true, category: 'forex' },
        { symbol: 'BTCUSD', bid: 43250.00, ask: 43275.00, change: 1250.00, changePercent: 2.98, volume: 125000, isFavorite: false, category: 'crypto' },
        { symbol: 'ETHUSD', bid: 2485.75, ask: 2487.25, change: 85.50, changePercent: 3.56, volume: 89000, isFavorite: true, category: 'crypto' }
      ];
      
      setSymbols(mockSymbols);
    } finally {
      setLoading(false);
    }
  };

  const connectToWebSocket = () => {
    let cleanupInterval = null;
    
    try {
      setConnectionStatus('connecting');
      
      // Connect to Zonex WebSocket for real-time price updates
      tradingApi?.connectWebSocket(
        (data) => handleWebSocketMessage(data),
        (error) => {
          console.error('WebSocket error:', error);
          setConnectionStatus('error');
          cleanupInterval = simulatePriceUpdates(); // Fallback to simulation
        },
        (event) => {
          console.log('WebSocket closed:', event);
          setConnectionStatus('disconnected');
        }
      );

      // Subscribe to price updates for all symbols
      const timeoutId = setTimeout(() => {
        if (tradingApi?.getConnectionStatus() === 'connected') {
          const symbolNames = symbols?.map(s => s?.symbol);
          tradingApi?.subscribeToPrices(symbolNames);
          setConnectionStatus('connected');
        } else {
          // Fallback to simulated updates
          cleanupInterval = simulatePriceUpdates();
        }
      }, 1500);

      return () => {
        clearTimeout(timeoutId);
        if (cleanupInterval) cleanupInterval();
      };
    } catch (error) {
      console.error('WebSocket connection failed:', error);
      setConnectionStatus('error');
      cleanupInterval = simulatePriceUpdates();
      
      return () => {
        if (cleanupInterval) cleanupInterval();
      };
    }
  };

  const handleWebSocketMessage = (data) => {
    switch (data?.type) {
      case 'price_update':
        updateSymbolPrice(data);
        break;
      case 'connection_status': setConnectionStatus(data?.status ||'connected');
        break;
      default:
        // Handle other message types as needed
        break;
    }
  };

  const updateSymbolPrice = (priceData) => {
    setSymbols(prev => 
      prev?.map(symbol => {
        if (symbol?.symbol === priceData?.symbol) {
          return {
            ...symbol,
            bid: parseFloat(priceData?.bid) || symbol?.bid,
            ask: parseFloat(priceData?.ask) || symbol?.ask,
            change: parseFloat(priceData?.change) || symbol?.change,
            changePercent: parseFloat(priceData?.changePercent) || symbol?.changePercent,
            volume: parseInt(priceData?.volume) || symbol?.volume
          };
        }
        return symbol;
      })
    );
  };

  // Simulate real-time price updates when WebSocket is not available
  const simulatePriceUpdates = () => {
    setConnectionStatus('simulated');
    
    const updateInterval = setInterval(() => {
      setSymbols(prev => 
        prev?.map(symbol => {
          const bidChange = (Math.random() - 0.5) * 0.001;
          const askChange = bidChange + (Math.random() * 0.0005);
          const newBid = Math.max(0, symbol?.bid + bidChange);
          const newAsk = Math.max(0, symbol?.ask + askChange);
          
          return {
            ...symbol,
            bid: newBid,
            ask: newAsk,
            change: symbol?.change + (Math.random() - 0.5) * 0.01,
            changePercent: symbol?.changePercent + (Math.random() - 0.5) * 0.05,
            volume: symbol?.volume + Math.floor((Math.random() - 0.5) * 10000)
          };
        })
      );
    }, 2000);

    return () => clearInterval(updateInterval);
  };

  const toggleFavorite = (symbolName) => {
    setSymbols(prev => 
      prev?.map(symbol => 
        symbol?.symbol === symbolName 
          ? { ...symbol, isFavorite: !symbol?.isFavorite }
          : symbol
      )
    );
    
    // TODO: Save favorite status to user preferences via API
  };

  const handleSymbolClick = (symbol) => {
    // Don't make XHR request, instead send WebSocket payload for chart data
    if (tradingApi?.ws && tradingApi?.getConnectionStatus() === 'connected') {
      const payload = {
        "symbols": false,
        "symbol": symbol?.symbol || "BTCUSD",
        "single": true,
        "chart": true,
        "period": "M1",
        "positions": true,
        "token": "92b79d3a-3d52-412c-ac67-00c5cdbf4bf5",
        "company": "zonex"
      };
      
      // Send payload via WebSocket
      tradingApi?.ws?.send(JSON.stringify(payload));
      
      // Call the parent callback to update selected symbol
      onSymbolSelect?.(symbol);
    } else {
      console.warn('WebSocket not connected, cannot send chart request');
      // Still update the selected symbol
      onSymbolSelect?.(symbol);
    }
  };

  // Category definitions
  const categories = [
    { 
      id: 'fx', 
      name: t('fx') || 'FX', 
      icon: 'DollarSign',
      filter: (s) => s.category === 'forex'
    },
    { 
      id: 'crypto', 
      name: t('crypto') || 'Crypto', 
      icon: 'Bitcoin',
      filter: (s) => s.category === 'crypto'
    },
    { 
      id: 'popular', 
      name: t('popular') || 'Popular', 
      icon: 'TrendingUp',
      filter: (s) => s.isFavorite || s.volume > 500000
    },
    { 
      id: 'stocks', 
      name: t('stocks') || 'Stocks', 
      icon: 'BarChart2',
      filter: (s) => s.category === 'stocks'
    },
    { 
      id: 'commodities', 
      name: t('commodities') || 'Commodities', 
      icon: 'Briefcase',
      filter: (s) => s.category === 'metals' || s.category === 'commodities'
    },
    { 
      id: 'indices', 
      name: t('indices') || 'Indices', 
      icon: 'FileText',
      filter: (s) => s.category === 'indices'
    }
  ];

  const filteredSymbols = symbols?.filter(symbol => {
    const matchesSearch = symbol?.symbol?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesTab = activeTab === 'all' || 
                      (activeTab === 'favorites' && symbol?.isFavorite);
    return matchesSearch && matchesTab;
  });

  // Group symbols by category
  const groupedSymbols = categories.map(category => ({
    ...category,
    symbols: filteredSymbols.filter(category.filter),
    count: filteredSymbols.filter(category.filter).length
  })).filter(cat => cat.count > 0); // Only show categories with symbols

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  // Get country flag emoji based on currency pair
  const getSymbolFlag = (symbol) => {
    const flagMap = {
      // Major Forex Pairs
      'EUR': '🇪🇺', 'USD': '🇺🇸', 'GBP': '🇬🇧', 'JPY': '🇯🇵', 'CHF': '🇨🇭',
      'CAD': '🇨🇦', 'AUD': '🇦🇺', 'NZD': '🇳🇿', 'CNY': '🇨🇳', 'HKD': '🇭🇰',
      'SGD': '🇸🇬', 'SEK': '🇸🇪', 'NOK': '🇳🇴', 'DKK': '🇩🇰', 'PLN': '🇵🇱',
      'TRY': '🇹🇷', 'ZAR': '🇿🇦', 'MXN': '🇲🇽', 'BRL': '🇧🇷', 'RUB': '🇷🇺',
      // Metals
      'XAU': '🥇', 'XAG': '🥈', 'XPT': '⚪', 'XPD': '⚫',
      // Crypto
      'BTC': '₿', 'ETH': 'Ξ', 'LTC': 'Ł', 'XRP': '✕', 'ADA': '₳',
      'DOT': '●', 'DOGE': 'Ð', 'SOL': '◎', 'MATIC': '⬡',
      // Indices
      'GER': '🇩🇪', 'US': '🇺🇸', 'UK': '🇬🇧', 'JP': '🇯🇵', 'HK': '🇭🇰',
      'FR': '🇫🇷', 'ES': '🇪🇸', 'IT': '🇮🇹', 'CH': '🇨🇭'
    };

    // Check for index patterns
    if (symbol?.includes('GER30') || symbol?.includes('DAX')) return '🇩🇪';
    if (symbol?.includes('US500') || symbol?.includes('SPX')) return '🇺🇸';
    if (symbol?.includes('UK100') || symbol?.includes('FTSE')) return '🇬🇧';
    if (symbol?.includes('JP225') || symbol?.includes('NIKKEI')) return '🇯🇵';
    if (symbol?.includes('HK50') || symbol?.includes('HSI')) return '🇭🇰';
    if (symbol?.includes('FR40') || symbol?.includes('CAC')) return '🇫🇷';

    // Extract first 3 letters for currency pairs
    const prefix = symbol?.substring(0, 3);
    
    // For crypto, check common patterns
    if (symbol?.includes('BTC')) return '₿';
    if (symbol?.includes('ETH')) return 'Ξ';
    if (symbol?.includes('LTC')) return 'Ł';
    if (symbol?.includes('XRP')) return '✕';
    if (symbol?.includes('ADA')) return '₳';
    if (symbol?.includes('DOT')) return '●';
    if (symbol?.includes('DOGE')) return 'Ð';
    if (symbol?.includes('SOL')) return '◎';
    
    // For metals
    if (symbol?.includes('XAU') || symbol?.includes('GOLD')) return '🥇';
    if (symbol?.includes('XAG') || symbol?.includes('SILVER')) return '🥈';
    
    return flagMap[prefix] || '🌐';
  };

  const formatPrice = (price) => {
    if (price > 1000) return price?.toFixed(1);
    if (price > 10) return price?.toFixed(3);
    return price?.toFixed(5);
  };

  const calculateSpread = (bid, ask) => {
    return (ask - bid)?.toFixed(5);
  };

  const getChangeColor = (change) => {
    return change >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
  };

  const getConnectionStatusColor = () => {
    switch (connectionStatus) {
      case 'connected': return 'text-green-500';
      case 'connecting': return 'text-yellow-500';
      case 'simulated': return 'text-blue-500';
      case 'error': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  const handleTradeClick = (symbol, type, event) => {
    event?.stopPropagation();
    setSelectedSymbolForTrade(symbol);
    setTradingType(type);
    setTradingModalOpen(true);
  };

  if (loading) {
    return (
      <div className={`h-full bg-card flex flex-col items-center justify-center ${isRTL ? 'rtl' : ''}`}>
        <Icon name="Loader" size={32} className="text-muted-foreground mb-4 animate-spin" />
        <p className="text-muted-foreground text-sm">{t('loadingSymbols') || 'Loading symbols...'}</p>
      </div>
    );
  }

  return (
    <div className={`h-full bg-card flex flex-col ${isRTL ? 'rtl' : ''}`}>
      {/* Header */}
      <div className="p-3 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-foreground">{t('marketWatch')}</h2>
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-1">
              <div className={`w-2 h-2 rounded-full ${
                connectionStatus === 'connected' ? 'bg-green-500' : 
                connectionStatus === 'connecting' ? 'bg-yellow-500' : 
                connectionStatus === 'simulated'? 'bg-blue-500' : 'bg-red-500'
              }`}></div>
              <span className={`text-xs ${getConnectionStatusColor()}`}>
                {connectionStatus === 'simulated' ? 'DEMO' : t(connectionStatus)}
              </span>
            </div>
            <Icon 
              name="Settings" 
              size={16} 
              className="text-muted-foreground cursor-pointer hover:text-foreground" 
              onClick={() => setSettingsModalOpen(true)}
            />
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-3 p-2 bg-yellow-500/10 border border-yellow-500/20 rounded text-xs text-yellow-600">
            {error}
          </div>
        )}

        {/* Search */}
        <div className="relative mb-3">
          <Icon name="Search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder={t('searchSymbols')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            className="w-full pl-9 pr-4 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 bg-muted p-1 rounded text-xs">
          <button
            onClick={() => setActiveTab('all')}
            className={`flex-1 py-1.5 px-2 font-medium rounded transition-colors ${
              activeTab === 'all' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t('all')}
          </button>
          <button
            onClick={() => setActiveTab('favorites')}
            className={`flex-1 py-1.5 px-2 font-medium rounded transition-colors ${
              activeTab === 'favorites' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t('favorites')}
          </button>
        </div>
      </div>

      {/* Column Headers - Clean, Minimal */}
      <div className="px-3 py-2 border-b border-border bg-gray-50 dark:bg-gray-900/50">
        <div className="grid grid-cols-2 gap-2 text-xs font-medium text-gray-600 dark:text-gray-400">
          <div>{t('symbol')}</div>
          <div className="text-right">{t('price')}</div>
        </div>
      </div>

      {/* Categorized Symbol List - Accordion Style */}
      <div className="flex-1 overflow-y-auto">
        {filteredSymbols?.length === 0 ? (
          <div className="flex items-center justify-center h-32">
            <div className="text-center">
              <Icon name="Search" size={32} className="text-muted-foreground mb-2 mx-auto" />
              <p className="text-sm text-muted-foreground">{t('noSymbolsFound') || 'No symbols found'}</p>
            </div>
          </div>
        ) : (
          groupedSymbols?.map((category) => (
            <div key={category.id} className="border-b border-border/30">
              {/* Category Header - Enhanced with #23827D */}
              <button
                onClick={() => toggleCategory(category.id)}
                className={`
                  w-full px-3 py-3.5 flex items-center justify-between 
                  transition-all duration-200 group
                  ${expandedCategories.includes(category.id)
                    ? 'bg-gradient-to-r from-[#23827D]/10 to-transparent dark:from-[#23827D]/5'
                    : 'hover:bg-gradient-to-r hover:from-[#23827D]/5 hover:to-transparent dark:hover:from-[#23827D]/5'
                  }
                `}
              >
                <div className="flex items-center gap-3">
                  <div className={`
                    p-1.5 rounded-lg transition-all duration-200
                    ${expandedCategories.includes(category.id)
                      ? 'bg-[#23827D]/20 dark:bg-[#23827D]/30'
                      : 'bg-gray-100 dark:bg-gray-800 group-hover:bg-[#23827D]/10'
                    }
                  `}>
                    <Icon 
                      name={category.icon} 
                      size={18} 
                      className={`
                        transition-colors duration-200
                        ${expandedCategories.includes(category.id)
                          ? 'text-[#23827D]'
                          : 'text-gray-600 dark:text-gray-400 group-hover:text-[#23827D]'
                        }
                      `}
                    />
                  </div>
                  <span className={`
                    text-sm font-bold transition-colors duration-200
                    ${expandedCategories.includes(category.id)
                      ? 'text-[#23827D]'
                      : 'text-foreground group-hover:text-[#23827D]'
                    }
                  `}>
                    {category.name}
                  </span>
                  <span className={`
                    px-2.5 py-1 text-xs font-bold rounded-full transition-all duration-200
                    ${expandedCategories.includes(category.id)
                      ? 'bg-[#23827D] text-white shadow-sm shadow-[#23827D]/20'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 group-hover:bg-[#23827D]/20 group-hover:text-[#23827D]'
                    }
                  `}>
                    {category.count}
                  </span>
                </div>
                <Icon 
                  name="ChevronDown" 
                  size={16} 
                  className={`
                    transition-all duration-300
                    ${expandedCategories.includes(category.id) 
                      ? 'transform rotate-180 text-[#23827D]' 
                      : 'text-gray-400 group-hover:text-[#23827D]'
                    }
                  `}
                />
              </button>

              {/* Category Symbols - Enhanced with #23827D */}
              {expandedCategories.includes(category.id) && (
                <div className="bg-white dark:bg-gray-950">
                  {category.symbols?.map((symbol) => (
                    <div
                      key={symbol?.symbol}
                      onClick={() => handleSymbolClick(symbol)}
                      className={`
                        cursor-pointer transition-all duration-200 border-b border-border/20 last:border-b-0 group
                        ${selectedSymbol?.symbol === symbol?.symbol 
                          ? 'bg-gradient-to-r from-[#23827D]/20 to-[#23827D]/5 dark:from-[#23827D]/20 dark:to-[#23827D]/5 border-l-4 border-l-[#23827D] shadow-sm' 
                          : 'hover:bg-gradient-to-r hover:from-[#23827D]/10 hover:to-transparent dark:hover:from-[#23827D]/10 hover:border-l-4 hover:border-l-[#23827D]/50'
                        }
                      `}
                    >
                      <div className={`px-3 py-3 ${selectedSymbol?.symbol === symbol?.symbol ? 'pl-9' : 'pl-12 group-hover:pl-9'} transition-all duration-200`}>
                        <div className="grid grid-cols-2 gap-2 items-center">
                          {/* Symbol Info with Flag */}
                          <div className="flex items-center space-x-2">
                            <div className="flex items-center gap-1.5">
                              <span className="text-xl leading-none" style={{ minWidth: '24px', textAlign: 'center' }}>
                                {getSymbolFlag(symbol?.symbol)}
                              </span>
                              <button
                                onClick={(e) => {
                                  e?.stopPropagation();
                                  toggleFavorite(symbol?.symbol);
                                }}
                                className="hover:scale-125 rounded p-0.5 transition-transform duration-200"
                              >
                                <Icon 
                                  name="Star" 
                                  size={12} 
                                  className={`
                                    transition-all duration-200
                                    ${symbol?.isFavorite 
                                      ? 'text-[#23827D] fill-current drop-shadow-sm' 
                                      : 'text-gray-300 dark:text-gray-600 group-hover:text-[#23827D]/50'
                                    }
                                  `}
                                />
                              </button>
                            </div>
                            <div className="min-w-0">
                              <div className={`
                                font-bold text-sm transition-colors duration-200
                                ${selectedSymbol?.symbol === symbol?.symbol
                                  ? 'text-[#23827D]'
                                  : 'text-foreground group-hover:text-[#23827D]'
                                }
                              `}>
                                {symbol?.symbol}
                              </div>
                              <div className={`font-mono text-xs font-semibold ${getChangeColor(symbol?.change)}`}>
                                {symbol?.change >= 0 ? '+' : ''}{symbol?.changePercent?.toFixed(2)}%
                              </div>
                            </div>
                          </div>
                          
                          {/* Price Info - Right Aligned with Enhanced Hover */}
                          <div className="text-right space-y-1">
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                onClick={(e) => handleTradeClick(symbol, 'sell', e)}
                                className="px-2.5 py-1 font-mono text-foreground font-bold text-xs hover:bg-red-500/20 hover:text-red-600 dark:hover:text-red-400 rounded transition-all duration-200 hover:scale-105 hover:shadow-sm"
                              >
                                {formatPrice(symbol?.bid)}
                              </button>
                              <span className="text-gray-300 dark:text-gray-700 font-bold">|</span>
                              <button
                                onClick={(e) => handleTradeClick(symbol, 'buy', e)}
                                className="px-2.5 py-1 font-mono text-foreground font-bold text-xs hover:bg-[#23827D]/20 hover:text-[#23827D] rounded transition-all duration-200 hover:scale-105 hover:shadow-sm"
                              >
                                {formatPrice(symbol?.ask)}
                              </button>
                            </div>
                            <div className={`
                              font-mono text-xs font-medium transition-colors duration-200
                              ${selectedSymbol?.symbol === symbol?.symbol
                                ? 'text-[#23827D]/70'
                                : 'text-gray-500 dark:text-gray-500 group-hover:text-[#23827D]/70'
                              }
                            `}>
                              {t('spread')}: {calculateSpread(symbol?.bid, symbol?.ask)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Trading Modal */}
      <TradingModal
        isOpen={tradingModalOpen}
        onClose={() => setTradingModalOpen(false)}
        type={tradingType}
        symbol={selectedSymbolForTrade}
      />

      {/* Settings Modal */}
      <MarketWatchSettingsModal
        isOpen={settingsModalOpen}
        onClose={() => setSettingsModalOpen(false)}
      />
    </div>
  );
};

export default SymbolList;